import type { CityConfig } from "@/pages/city-template/types";

export const benningtonConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Vermont Department of Health Vital Records in Burlington. Required for first-time passport applications and child passports.", url: "https://www.healthvermont.gov/vital-records" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Vermont DPH Vital Records office. Required for estate and legal matters.", url: "https://www.healthvermont.gov/vital-records" },
    { icon: "ri-hearts-line", label: "Marriage License", desc: "Request marriage license records from the Bennington Town Clerk. Required for name change passport applications.", url: "https://www.benningtonvt.org/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Bennington County Superior Court. Required for name change passport corrections.", url: "https://www.vermontjudiciary.org/" },
  ],
  city: "Bennington",
  state: "Vermont",
  stateAbbr: "VT",
  slug: "bennington",
  route: "/cities/bennington",
  title: "Expedited Passport Services in Bennington, VT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Bennington, VT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving Bennington, North Bennington, Shaftsbury, Arlington, and all of Southwestern Vermont.",
  h1: "Expedited Passport Services in Bennington, VT",
  heroSubtitle:
    "Bennington travelers trust Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No trip to the Vermont Passport Agency required.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Historic%20Bennington%20Vermont%20featuring%20iconic%20Bennington%20Battle%20Monument%20towering%20over%20the%20town%2C%20charming%20downtown%20with%20historic%20buildings%20and%20covered%20bridges%2C%20lush%20green%20hills%20of%20the%20Taconic%20Mountains%2C%20warm%20golden%20hour%20light%2C%20classic%20New%20England%20small%20town%20atmosphere%2C%20scenic%20Vermont%20countryside%20with%20rolling%20hills%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=vt-ben-hero-001&orientation=landscape",
  heroImageAlt: "Bennington Vermont with historic Battle Monument and downtown",
  geoPosition: "42.8784; -73.1968",
  geoPlacename: "Bennington, Vermont, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Vermont Passport Agency",
    address: "50 South Main Street, St. Albans, VT 05478",
    distance: "~170 miles from Bennington via I-87",
  },
  nearbyCitiesZips: [
    { name: "Bennington", zips: "05201, 05257" },
    { name: "North Bennington", zips: "05257" },
    { name: "Shaftsbury", zips: "05262" },
    { name: "Arlington", zips: "05250" },
    { name: "Manchester", zips: "05254, 05255" },
    { name: "Dorset", zips: "05251" },
    { name: "Woodford", zips: "05201" },
    { name: "Pownal", zips: "05261" },
    { name: "Stamford", zips: "05352" },
    { name: "Readsboro", zips: "05350" },
    { name: "Sunderland", zips: "05250" },
    { name: "Peru", zips: "05152" },
    { name: "Londonderry", zips: "05148" },
    { name: "Weston", zips: "05161" },
    { name: "Winhall", zips: "05340" },
    { name: "Brattleboro", zips: "05301, 05302, 05303" },
    { name: "Rutland", zips: "05701, 05702" },
    { name: "Burlington", zips: "05401, 05402, 05405, 05406" },
    { name: "Montpelier", zips: "05601, 05602, 05603, 05604, 05609, 05620, 05633" },
    { name: "Stowe", zips: "05672" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Bennington travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Southwestern Vermont",
      desc: "Bennington, North Bennington, Shaftsbury, Arlington, Manchester, and all surrounding Southwestern Vermont communities.",
    },
  ],
  testimonials: [
    {
      name: "Melissa H.",
      location: "Bennington, VT",
      rating: 5,
      text: "I needed my passport renewed for a trip to Italy in 5 days. Fast Passport Center had it back to me in Bennington in 3 days. Their Southwestern Vermont service is outstanding.",
      service: "Passport Renewal",
    },
    {
      name: "Steve P.",
      location: "Manchester, VT",
      rating: 5,
      text: "Applied for my first passport through Fast Passport Center. They helped me navigate the process from Manchester and I had my passport in 4 days. Great experience.",
      service: "New Passport",
    },
    {
      name: "Nancy W.",
      location: "Arlington, VT",
      rating: 5,
      text: "My daughter needed a passport for a school trip to France. Fast Passport Center handled the child passport process and had it ready in 4 days. Arlington families can trust them.",
      service: "Child Passport",
    },
    {
      name: "Greg F.",
      location: "Shaftsbury, VT",
      rating: 5,
      text: "Lost my passport before a business trip to Germany. Fast Passport Center replaced it in 3 days and saved my trip. Shaftsbury residents should use them for emergencies.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "ben-1",
      name: "Bennington Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "118 South Street",
      city: "Bennington",
      state: "VT",
      zip: "05201",
      phone: "802-442-6291",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Bennington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "ben-2",
      name: "Manchester Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "11 School Street",
      city: "Manchester",
      state: "VT",
      zip: "05254",
      phone: "802-362-1456",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Manchester Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "ben-3",
      name: "Arlington Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "10 Church Street",
      city: "Arlington",
      state: "VT",
      zip: "05250",
      phone: "802-375-6075",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Arlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "ben-4",
      name: "Shaftsbury Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "2231 Shaftsbury Hollow Road",
      city: "Shaftsbury",
      state: "VT",
      zip: "05262",
      phone: "802-442-4426",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Shaftsbury",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "ben-5",
      name: "North Bennington Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "134 Main Street",
      city: "North Bennington",
      state: "VT",
      zip: "05257",
      phone: "802-442-9031",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "North Bennington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "ben-6",
      name: "Bennington Town Clerk",
      type: "Town Clerk / Passport Services",
      address: "118 South Street",
      city: "Bennington",
      state: "VT",
      zip: "05201",
      phone: "802-442-1037",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "Bennington Municipal Center",
      website: "https://www.benningtonvt.org/",
      websiteLabel: "Town of Bennington",
      notes: "Provides passport application acceptance services. Appointment recommended.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Bennington, VT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the Vermont Passport Agency if I live in Bennington?",
      a: "No — if you use Fast Passport Center, you don't need to visit the Vermont Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Bennington?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Bennington, Manchester, and Arlington. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Bennington?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Bennington?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Bennington?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Bennington. We serve Bennington and all of Southwestern Vermont through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to Bennington for international travel?",
      a: "Albany International (ALB) is the closest at ~40 miles. Bradley International (BDL) in Hartford is ~85 miles, Boston Logan (BOS) is ~140 miles, and Burlington (BTV) is ~120 miles. Most Bennington residents use Albany for international travel.",
    },
    {
      q: "How much does expedited passport service cost in Bennington?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in Bennington?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in Bennington?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in Bennington?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does Bennington have a local passport agency?",
      a: "No, Bennington does not have a regional passport agency. The nearest regional agency is the Vermont Passport Agency at 10 Causeway St Suite 247, approximately 180 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Bennington", address: "various CVS locations in Bennington", hours: "Mon-Sun, hours vary by location", website: "https://www.cvs.com/" },
    { name: "Walgreens — Bennington", address: "various Walgreens locations in Bennington", hours: "Mon-Sun, hours vary by location", website: "https://www.walgreens.com/" },
    { name: "UPS Store — Bennington", address: "various UPS Store locations in Bennington", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM", website: "https://www.theupsstore.com/" },
    { name: "FedEx Office — Bennington", address: "various FedEx Office locations in Bennington", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM", website: "https://www.fedex.com/" },
    { name: "Walmart — Bennington", address: "various Walmart locations in Bennington", hours: "Mon-Sun 7:00 AM - 10:00 PM", website: "https://www.walmart.com/" },
  ],
  dmvLocations: [
    {
      name: "VT DMV — Bennington",
      address: "1233 S. Main Street, Bennington, VT 05201",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications. Closest DMV to Bennington.",
      featured: true,
    },
    {
      name: "VT DMV — Rutland",
      address: "92 State Street, Rutland, VT 05701",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Alternative DMV location for Southwestern Vermont residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "ALB",
      name: "Albany International Airport",
      distance: "~40 miles from Bennington",
      address: "737 Albany Shaker Rd, Albany, NY 12211",
      note: "Closest airport to Bennington with flights to many domestic and international destinations. Most Bennington residents use Albany.",
      flightUrl: "https://www.albanyairport.com/",
      flightLabel: "Albany International",
      featured: true,
    },
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~85 miles from Bennington",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Alternative for Bennington travelers with flights to many domestic and international destinations.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: false,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "~140 miles from Bennington",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with flights to virtually every global destination. Common for Bennington residents on international trips.",
      flightUrl: "https://www.massport.com/logan-airport/",
      flightLabel: "Logan International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Bennington travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Bennington residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Bennington residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Bennington residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Bennington, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Bennington residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Bennington travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Bennington residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Bennington residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Bennington residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Bennington." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Bennington." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Bennington travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Bennington travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Bennington team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Bennington address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Bennington-Specific Guidance", agency: "Generic — no Southwestern Vermont or Bennington County expertise", fpc: "Full Bennington/Southwestern Vermont knowledge built in" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Burlington", route: "/cities/burlington-vt" },
    { name: "Montpelier", route: "/montpelier" },
    { name: "Rutland", route: "/cities/rutland" },
    { name: "Stowe", route: "/cities/stowe" },
    { name: "Brattleboro", route: "/cities/brattleboro" },
    { name: "Bennington", route: "/cities/bennington" },
    { name: "Middlebury", route: "/cities/middlebury" },
    { name: "Woodstock", route: "/cities/woodstock-vt" },
    { name: "Springfield", route: "/cities/springfield-vt" },
    { name: "White River Junction", route: "/cities/white-river-junction" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Bennington and all of Southwestern Vermont as a remote courier service. We do not have a physical office in Bennington, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "50 South Main Street, St. Albans, VT 05478",
  fpcOfficeDistance: "Remote courier service — Bennington, VT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all Bennington neighborhoods and surrounding Southwestern Vermont communities. From downtown to North Bennington, from the historic village to the mountain towns — we serve every Bennington community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve Bennington and all surrounding Southwestern Vermont communities. Whether you're in downtown Bennington or a nearby town, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our Bennington customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes Bennington travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "Bennington and Southwestern Vermont travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in Bennington, VT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/vermont",
  statePageName: "Vermont Passport Services",
  dmvWebsite: "https://dmv.vermont.gov",
  dmvLabel: "Vermont DMV — Official Website",
};

export default benningtonConfig;